import { Button, Input, Label, cn } from "@devhop/ui";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import type { EventFxRatesDTO } from "../../domain/contracts";
import { useUpdateEventFxRatesMutation } from "../api/queries";
import { formatPerformanceDateTime } from "./PerformanceReportsNav";

type EventFxRatesCardProps = {
  eventId: string;
  fxRates: EventFxRatesDTO;
  canEdit: boolean;
  needsUsdRate: boolean;
  needsThbRate: boolean;
};

function parseRateInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed.replace(/,/g, ""), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

function rateToInput(value: number | null): string {
  return value != null ? String(value) : "";
}

export function EventFxRatesCard({
  eventId,
  fxRates,
  canEdit,
  needsUsdRate,
  needsThbRate,
}: EventFxRatesCardProps) {
  const updateFxRates = useUpdateEventFxRatesMutation(eventId);
  const [usdRate, setUsdRate] = useState(rateToInput(fxRates.fxUsdToLak));
  const [thbRate, setThbRate] = useState(rateToInput(fxRates.fxThbToLak));
  const [note, setNote] = useState(fxRates.fxRateNote ?? "");

  useEffect(() => {
    setUsdRate(rateToInput(fxRates.fxUsdToLak));
    setThbRate(rateToInput(fxRates.fxThbToLak));
    setNote(fxRates.fxRateNote ?? "");
  }, [fxRates]);

  const handleSave = () => {
    updateFxRates.mutate({
      fxUsdToLak: parseRateInput(usdRate),
      fxThbToLak: parseRateInput(thbRate),
      fxRateNote: note.trim() ? note.trim() : null,
    });
  };

  const handleClear = () => {
    updateFxRates.mutate({
      fxUsdToLak: null,
      fxThbToLak: null,
      fxRateNote: null,
    });
  };

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-5 py-4">
        <p className="font-semibold text-sm">ອັດຕາແລກປ່ຽນ (ສຳລັບກຳໄລ)</p>
        <p className="mt-1 text-muted-foreground text-xs">
          ປ້ອນອັດຕາ LAK ຕໍ່ 1 USD / 1 THB ເພື່ອຄິດກຳໄລເທົ່າຄ່າ
        </p>
      </div>

      <div className="space-y-4 px-5 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <RateField
            id="fx-usd-rate"
            label="1 USD ="
            suffix="ກີບ"
            value={usdRate}
            onChange={setUsdRate}
            disabled={!canEdit || updateFxRates.isPending}
            hint={needsUsdRate ? "ມີລາຍຮັບ USD — ຄວນປ້ອນອັດຕາ" : undefined}
            emphasized={needsUsdRate && fxRates.fxUsdToLak == null}
          />
          <RateField
            id="fx-thb-rate"
            label="1 THB ="
            suffix="ກີບ"
            value={thbRate}
            onChange={setThbRate}
            disabled={!canEdit || updateFxRates.isPending}
            hint={needsThbRate ? "ມີລາຍຮັບ THB — ຄວນປ້ອນອັດຕາ" : undefined}
            emphasized={needsThbRate && fxRates.fxThbToLak == null}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="fx-rate-note">ຫມາຍເຫດ</Label>
          <Input
            id="fx-rate-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="BCEL 22/08/2025"
            disabled={!canEdit || updateFxRates.isPending}
            maxLength={500}
          />
        </div>

        {fxRates.fxRateUpdatedAt ? (
          <p className="text-muted-foreground text-xs">
            ອັບເດດຄັ້ງລ່າສຸດ{" "}
            {formatPerformanceDateTime(fxRates.fxRateUpdatedAt)}
          </p>
        ) : null}

        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={updateFxRates.isPending}
            >
              {updateFxRates.isPending ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : null}
              ບັນທຶກອັດຕາ
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleClear}
              disabled={updateFxRates.isPending}
            >
              ລ້າງອັດຕາ
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            ບໍ່ມີສິດແກ້ໄຂອັດຕາແລກປ່ຽນ
          </p>
        )}
      </div>
    </section>
  );
}

function RateField({
  id,
  label,
  suffix,
  value,
  onChange,
  disabled,
  hint,
  emphasized,
}: {
  id: string;
  label: string;
  suffix: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: string;
  emphasized?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          inputMode="numeric"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="—"
          disabled={disabled}
          className={cn(emphasized && "border-amber-500/50")}
        />
        <span className="shrink-0 text-muted-foreground text-sm">{suffix}</span>
      </div>
      {hint ? (
        <p className="text-amber-700 text-xs dark:text-amber-300">{hint}</p>
      ) : null}
    </div>
  );
}
